import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemText,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
} from '@mui/material';
import {
  TrendingUp as ImprovedIcon,
  Warning as AreaIcon,
  CompareArrows as CompareIcon,
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import useStore from '../store/useStore';
import { translations } from '../translations';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const THEME_COLORS = [
  '#C2233E',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#3b82f6',
  '#7A4988',
  '#ec4899',
  '#14b8a6',
];

function ThemeGrowthChart({ themes }) {
  // A unit with a single point has no growth to show; keep the chart readable by
  // plotting the units that changed most.
  const entries = Object.entries(themes)
    .filter(([, records]) => records.length >= 2)
    .sort(
      ([, a], [, b]) =>
        Math.abs(b[b.length - 1].mastery - b[0].mastery) -
        Math.abs(a[a.length - 1].mastery - a[0].mastery)
    )
    .slice(0, 8);
  // Collect all unique dates, sorted
  const allDates = [
    ...new Set(entries.flatMap(([, records]) => records.map((r) => r.date))),
  ].sort();
  const labels = allDates.map((d) => new Date(d).toLocaleDateString());

  const datasets = entries.map(([name, records], idx) => {
    const byDate = Object.fromEntries(records.map((r) => [r.date, r.mastery]));
    return {
      label: name,
      data: allDates.map((d) => byDate[d] ?? null),
      borderColor: THEME_COLORS[idx % THEME_COLORS.length],
      backgroundColor: THEME_COLORS[idx % THEME_COLORS.length] + '22',
      tension: 0.3,
      fill: false,
      spanGaps: true,
      pointRadius: 4,
      pointHoverRadius: 6,
    };
  });

  return (
    <Line
      data={{ labels, datasets }}
      options={{
        responsive: true,
        plugins: {
          legend: { position: 'bottom', labels: { boxWidth: 12 } },
          tooltip: {
            callbacks: {
              label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y}%`,
            },
          },
        },
        scales: {
          y: {
            min: 0,
            max: 100,
            ticks: { callback: (v) => `${v}%` },
            grid: { color: 'rgba(0,0,0,0.06)' },
          },
          x: { grid: { display: false } },
        },
      }}
    />
  );
}

export default function ProgressComparison({
  studentId,
  onReassessWeakThemes,
}) {
  const [profiles, setProfiles] = useState([]);
  const [initialId, setInitialId] = useState('');
  const [finalId, setFinalId] = useState('');
  const [comparison, setComparison] = useState(null);
  const [themeGrowth, setThemeGrowth] = useState(null);
  const [loadError, setLoadError] = useState('');

  const { language } = useStore();
  const t = translations[language];

  useEffect(() => {
    fetch(`/api/profiles/student/${studentId}?all=true&active=1`, {
      credentials: 'include',
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
        return Array.isArray(data) ? data : [];
      })
      .then((data) =>
        setProfiles(
          data
            .filter((p) => p.excluded !== true)
            .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
        )
      )
      .catch((err) => setLoadError(err.message));

    // Load theme growth data
    fetch(`/api/analytics/theme-growth/${studentId}`, {
      credentials: 'include',
    })
      .then((res) => (res.ok ? res.json() : null))
      .then(setThemeGrowth)
      .catch(() => {});
  }, [studentId]);

  const tmplIdOf = (p) =>
    String(
      (typeof p?.templateId === 'object'
        ? p?.templateId?._id
        : p?.templateId) ?? ''
    );
  const typeLabel = (type) =>
    language === 'en'
      ? type
      : type === 'phrase'
        ? 'фраза'
        : type === 'word'
          ? 'слово'
          : type;

  useEffect(() => {
    if (initialId && finalId) generateComparison();
  }, [initialId, finalId]);

  const generateComparison = () => {
    const initial = profiles.find((p) => p._id === initialId);
    const final_ = profiles.find((p) => p._id === finalId);
    if (!initial || !final_) return;

    const improved = [];
    const needsFocus = [];
    const themeBreakdown = [];

    // Compare ONLY what both assessments cover: units present in both and,
    // inside them, items present in both. A unit that was added later (or a partial
    // assessment of other units) must not look like growth from zero.
    let commonScoreNew = 0;
    let commonScoreOld = 0;
    let commonMax = 0;
    let finalUnits = final_.themes.length;
    let commonUnits = 0;

    final_.themes.forEach((fTheme) => {
      const iTheme = initial.themes.find(
        (t) =>
          (t.themeId &&
            fTheme.themeId &&
            String(t.themeId) === String(fTheme.themeId)) ||
          t.themeName === fTheme.themeName
      );
      if (!iTheme) return;
      commonUnits += 1;

      let fScore = 0;
      let iScore = 0;
      let max = 0;
      fTheme.items.forEach((fItem) => {
        const iItem = iTheme.items.find(
          (i) =>
            (i.itemId &&
              fItem.itemId &&
              String(i.itemId) === String(fItem.itemId)) ||
            i.itemText === fItem.itemText
        );
        if (!iItem) return;
        fScore += fItem.score;
        iScore += iItem.score;
        max += 2;

        const itemData = {
          name: fItem.itemText,
          type: fItem.itemType,
          from: iItem.score,
          to: fItem.score,
          theme: fTheme.themeName,
        };
        if (fItem.score > iItem.score) improved.push(itemData);
        if (fItem.score < 2) needsFocus.push(itemData);
      });
      if (max === 0) return;

      commonScoreNew += fScore;
      commonScoreOld += iScore;
      commonMax += max;

      const fPct = Math.round((fScore / max) * 100);
      const iPct = Math.round((iScore / max) * 100);
      themeBreakdown.push({
        themeName: fTheme.themeName,
        basePct: iPct,
        newPct: fPct,
        change: fPct - iPct,
        baseScore: iScore,
        baseMax: max,
        newScore: fScore,
        newMax: max,
      });
    });

    setComparison({
      scoreDiff: commonScoreNew - commonScoreOld,
      masteryDiff:
        commonMax > 0
          ? Math.round((commonScoreNew / commonMax) * 100) -
            Math.round((commonScoreOld / commonMax) * 100)
          : 0,
      commonUnits,
      finalUnits,
      commonItems: commonMax / 2,
      improved,
      needsFocus,
      themeBreakdown: themeBreakdown.sort((a, b) => b.change - a.change),
    });
  };

  const baseProfile = profiles.find((p) => p._id === initialId);
  // Smart filtering: second dropdown only shows profiles on the SAME template
  const compatibleProfiles = initialId
    ? profiles.filter((p) => {
        const bTmpl =
          typeof baseProfile?.templateId === 'object'
            ? baseProfile?.templateId?._id
            : baseProfile?.templateId;
        const pTmpl =
          typeof p.templateId === 'object' ? p.templateId?._id : p.templateId;
        return p._id !== initialId && String(bTmpl) === String(pTmpl);
      })
    : profiles;

  const templateNames = [
    ...new Set(
      profiles.map((p) =>
        typeof p.templateId === 'object' ? p.templateId?.name : '(no template)'
      )
    ),
  ].filter(Boolean);

  function profileLabel(p) {
    const tmpl = typeof p.templateId === 'object' ? p.templateId?.name : null;
    const date = p.profileName || new Date(p.createdAt).toLocaleDateString();
    const ver = p.version > 1 ? ` v${p.version}` : '';
    return tmpl ? `[${tmpl}] ${date}${ver}` : `${date}${ver}`;
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
        {language === 'en' ? 'Progress Delta Analysis' : 'Анализ прогресса'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {language === 'en'
          ? 'Select two assessments on the same template for a fair comparison. Only compatible assessments are shown in the second dropdown.'
          : 'Выберите две оценки по одному шаблону. Второй список фильтруется автоматически.'}
      </Typography>
      {loadError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {language === 'en'
            ? `Could not load assessments: ${loadError}`
            : `Не удалось загрузить оценки: ${loadError}`}
        </Alert>
      )}

      {templateNames.length > 1 && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {templateNames.map((name) => (
            <Chip
              key={name}
              label={name}
              size="small"
              variant="outlined"
              color="primary"
              sx={{ fontWeight: 600 }}
            />
          ))}
        </Box>
      )}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth>
            <InputLabel>
              {language === 'en' ? 'Base Assessment (Older)' : 'Базовая оценка'}
            </InputLabel>
            <Select
              value={initialId}
              label={
                language === 'en' ? 'Base Assessment (Older)' : 'Базовая оценка'
              }
              onChange={(e) => {
                setInitialId(e.target.value);
                setFinalId(''); // reset second when first changes
              }}
            >
              <MenuItem value="">
                <em>{language === 'en' ? 'Select…' : 'Выбрать…'}</em>
              </MenuItem>
              {profiles.map((p) => (
                <MenuItem key={p._id} value={p._id}>
                  {profileLabel(p)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth disabled={!initialId}>
            <InputLabel>
              {language === 'en'
                ? initialId
                  ? `Comparison (same template only)`
                  : 'Select base first'
                : initialId
                  ? 'Сравниваемая (тот же шаблон)'
                  : 'Сначала выберите базовую'}
            </InputLabel>
            <Select
              value={finalId}
              label={
                language === 'en'
                  ? initialId
                    ? 'Comparison (same template only)'
                    : 'Select base first'
                  : initialId
                    ? 'Сравниваемая (тот же шаблон)'
                    : 'Сначала выберите базовую'
              }
              onChange={(e) => setFinalId(e.target.value)}
            >
              <MenuItem value="">
                <em>{language === 'en' ? 'Select…' : 'Выбрать…'}</em>
              </MenuItem>
              {compatibleProfiles.length === 0 && initialId ? (
                <MenuItem disabled>
                  {language === 'en'
                    ? 'No other assessments on this template'
                    : 'Нет других оценок по этому шаблону'}
                </MenuItem>
              ) : (
                compatibleProfiles.map((p) => (
                  <MenuItem key={p._id} value={p._id}>
                    {profileLabel(p)}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          {initialId && compatibleProfiles.length === 0 && (
            <Typography
              variant="caption"
              color="warning.main"
              sx={{ mt: 0.5, display: 'block' }}
            >
              {language === 'en'
                ? 'Create a reassessment first to enable comparison.'
                : 'Сначала создайте переоценку для сравнения.'}
            </Typography>
          )}
        </Grid>
      </Grid>

      {comparison && (
        <>
          <Alert
            severity={
              comparison.commonUnits === 0 || comparison.commonItems < 10
                ? 'warning'
                : 'info'
            }
            variant="outlined"
            sx={{ mb: 3, borderRadius: 2 }}
          >
            {comparison.commonUnits === 0
              ? language === 'en'
                ? 'These assessments share no units, so there is nothing to compare.'
                : 'В этих оценках нет общих тем — сравнивать нечего.'
              : language === 'en'
                ? `Compared on ${comparison.commonUnits} shared unit(s), ${comparison.commonItems} items. Units or words present in only one assessment are left out.${comparison.commonItems < 10 ? ' Very few items — treat the change as approximate.' : ''}`
                : `Сравнение по общим темам: ${comparison.commonUnits}, элементов: ${comparison.commonItems}. Темы и слова, которые есть только в одной оценке, не учитываются.${comparison.commonItems < 10 ? ' Элементов очень мало — изменение приблизительное.' : ''}`}
          </Alert>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper
                sx={{
                  p: 3,
                  textAlign: 'center',
                  bgcolor: 'primary.50',
                  borderRadius: 4,
                }}
              >
                <Typography
                  variant="h3"
                  color="primary.main"
                  sx={{ fontWeight: 800 }}
                >
                  {comparison.masteryDiff > 0 ? '+' : ''}
                  {comparison.masteryDiff}
                  {language === 'en' ? ' pp' : ' п.п.'}
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {language === 'en' ? 'MASTERY GROWTH' : 'РОСТ'}
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper sx={{ p: 3, height: '100%', borderRadius: 4 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <ImprovedIcon color="success" />{' '}
                  {language === 'en' ? 'Breakthroughs' : 'Прорывы'}
                </Typography>
                <List dense>
                  {comparison.improved.slice(0, 5).map((item, i) => (
                    <ListItem key={i} sx={{ px: 0 }}>
                      <ListItemText
                        primary={item.name}
                        secondary={`${item.theme} (${typeLabel(item.type)})`}
                      />
                      <Chip
                        label={`+${item.to - item.from}`}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    </ListItem>
                  ))}
                  {comparison.improved.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      {language === 'en'
                        ? 'No breakthroughs detected'
                        : 'Прорывов не обнаружено'}
                    </Typography>
                  )}
                </List>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper sx={{ p: 3, height: '100%', borderRadius: 4 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <AreaIcon color="warning" />{' '}
                  {language === 'en'
                    ? 'Next Focus Areas'
                    : 'Области для работы'}
                </Typography>
                <List dense>
                  {comparison.needsFocus.slice(0, 5).map((item, i) => (
                    <ListItem key={i} sx={{ px: 0 }}>
                      <ListItemText
                        primary={item.name}
                        secondary={`${item.theme} (${typeLabel(item.type)})`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          </Grid>

          {/* Reassess Weak Themes */}
          {onReassessWeakThemes &&
            comparison.themeBreakdown.some((r) => r.newPct < 50) && (
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={() => {
                    const weak = comparison.themeBreakdown
                      .filter((r) => r.newPct < 50)
                      .map((r) => r.themeName);
                    // Weak themes come from the compared assessment; the page
                    // re-assesses them on the latest profile of its template.
                    const source = profiles.find((p) => p._id === finalId);
                    onReassessWeakThemes({
                      profileId: finalId,
                      templateId: tmplIdOf(source),
                      themeNames: weak,
                    });
                  }}
                >
                  {language === 'en'
                    ? `Reassess Weak Themes (${comparison.themeBreakdown.filter((r) => r.newPct < 50).length})`
                    : `Переоценить слабые темы (${comparison.themeBreakdown.filter((r) => r.newPct < 50).length})`}
                </Button>
              </Box>
            )}

          {/* Theme-by-Theme Breakdown Table */}
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            {language === 'en'
              ? 'Theme-by-Theme Breakdown'
              : 'Разбивка по темам'}
          </Typography>
          <TableContainer component={Paper} sx={{ borderRadius: 3, mb: 4 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {language === 'en' ? 'Theme' : 'Тема'}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    {language === 'en' ? 'Base' : 'Базовая'}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    {language === 'en' ? 'New' : 'Новая'}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    {language === 'en' ? 'Change' : 'Изменение'}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '30%' }}>
                    {language === 'en' ? 'Progress' : 'Прогресс'}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {comparison.themeBreakdown.map((row, i) => (
                  <TableRow
                    key={i}
                    sx={{
                      bgcolor:
                        row.change > 0
                          ? 'success.50'
                          : row.change < 0
                            ? 'error.50'
                            : 'inherit',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>
                      {row.themeName}
                    </TableCell>
                    <TableCell align="center">{row.basePct}%</TableCell>
                    <TableCell align="center">{row.newPct}%</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`${row.change > 0 ? '+' : ''}${row.change}%`}
                        size="small"
                        color={
                          row.change > 0
                            ? 'success'
                            : row.change < 0
                              ? 'error'
                              : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <LinearProgress
                        variant="determinate"
                        value={row.newPct}
                        color={
                          row.newPct >= 80
                            ? 'success'
                            : row.newPct >= 50
                              ? 'warning'
                              : 'error'
                        }
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Theme Growth Over Time */}
      {themeGrowth && Object.keys(themeGrowth.themes || {}).length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            {language === 'en' ? 'Theme Growth Over Time' : 'Рост по темам'}
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 3 }}>
            <ThemeGrowthChart themes={themeGrowth.themes} />
          </Paper>
          {Object.entries(themeGrowth.themes)
            .sort(([, a], [, b]) => {
              const d = (r) =>
                r.length >= 2 ? r[r.length - 1].mastery - r[0].mastery : -999;
              return d(b) - d(a);
            })
            .map(([themeName, records]) => {
              const first = records[0]?.mastery ?? 0;
              const last = records[records.length - 1]?.mastery ?? 0;
              const delta = last - first;
              return (
                <Paper
                  key={themeName}
                  variant="outlined"
                  sx={{ p: 2, mb: 1.5, borderRadius: 3 }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, flexGrow: 1 }}
                    >
                      {themeName}
                    </Typography>
                    <Chip
                      label={`${last}%`}
                      size="small"
                      color={
                        last >= 80
                          ? 'success'
                          : last >= 50
                            ? 'warning'
                            : 'error'
                      }
                    />
                    {records.length >= 2 && (
                      <Chip
                        label={`${delta > 0 ? '+' : ''}${delta} ${language === 'en' ? 'pp' : 'п.п.'}`}
                        size="small"
                        color={
                          delta > 0
                            ? 'success'
                            : delta < 0
                              ? 'error'
                              : 'default'
                        }
                        variant="outlined"
                      />
                    )}
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(last, 100)}
                    color={
                      last >= 80 ? 'success' : last >= 50 ? 'warning' : 'error'
                    }
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Paper>
              );
            })}
        </Box>
      )}
    </Box>
  );
}
